import { useState, useEffect } from "react";
import { fetchAdminSupport, updateAdminSupport } from "../services/apiService";

const CATEGORIES = {
  crash: { emoji: "💥", label: "App froze/crashed" },
  purchase: { emoji: "💳", label: "Purchase" },
  ads: { emoji: "📺", label: "Ads" },
  transfer: { emoji: "🔄", label: "Transfer progress" },
  feature: { emoji: "💡", label: "Feature request" },
  other: { emoji: "❓", label: "Other" },
};

const STATUSES = [
  { id: "new", label: "Mới", color: "#6B7280" },
  { id: "seen", label: "Đã xem", color: "#3B82F6" },
  { id: "working", label: "Đang xử lý", color: "#F59E0B" },
  { id: "done", label: "Xong", color: "#10B981" },
];

const APP_NAMES = {
  "com.mintlab.fidgettoys": "Fidget Toys",
  "com.mintlab.glowdoodle": "Glow Doodle",
  "com.memefx.brainrot.soundboard": "Brainrot Soundboard",
};

export default function AdminSupportView() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchAdminSupport().then((data) => {
      if (data === "forbidden") setForbidden(true);
      else if (data) setTickets(data);
      setLoading(false);
    });
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    const result = await updateAdminSupport(id, { status: newStatus });
    if (result) {
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status: newStatus } : t)));
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-muted)" }}>Đang tải...</div>;
  }

  if (forbidden) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: "48px", marginBottom: "12px" }}>🔒</div>
        <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--text-primary)" }}>Không có quyền truy cập</div>
        <div style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "8px" }}>Trang này chỉ dành cho admin.</div>
      </div>
    );
  }

  const filtered = filterStatus === "all" ? tickets : tickets.filter((t) => t.status === filterStatus);

  return (
    <div style={{ padding: "16px 4px 80px" }}>
      <h2 style={{ fontSize: "20px", fontWeight: "800", color: "var(--text-primary)", margin: "0 0 4px" }}>
        🛟 Hỗ trợ ({tickets.length})
      </h2>
      <p style={{ fontSize: "13px", color: "var(--text-muted)", margin: "0 0 16px" }}>
        Ticket gửi từ form Support công khai trên các app.
      </p>

      <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
        {[{ id: "all", label: "Tất cả" }, ...STATUSES].map((s) => (
          <button
            key={s.id}
            onClick={() => setFilterStatus(s.id)}
            style={{
              padding: "6px 12px", borderRadius: "999px", fontSize: "13px", fontWeight: "600",
              cursor: "pointer", border: "1px solid var(--border-secondary)",
              background: filterStatus === s.id ? "var(--text-primary)" : "transparent",
              color: filterStatus === s.id ? "var(--bg-primary)" : "var(--text-muted)",
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-muted)" }}>Chưa có ticket nào.</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {filtered.map((t) => {
          const cat = CATEGORIES[t.category] || { emoji: "❓", label: t.category };
          const st = STATUSES.find((s) => s.id === t.status) || STATUSES[0];
          return (
            <div
              key={t.id}
              style={{
                background: "var(--bg-secondary)", border: "1px solid var(--border-secondary)",
                borderRadius: "14px", padding: "16px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)" }}>
                  {cat.emoji} {cat.label}
                </span>
                <span style={{ fontSize: "11px", color: st.color, fontWeight: "700", border: `1px solid ${st.color}`, borderRadius: "999px", padding: "2px 8px" }}>
                  {st.label}
                </span>
              </div>

              <div style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>
                ✉️ <a href={`mailto:${t.email}`} style={{ color: "var(--text-primary)" }}>{t.email}</a>
                {t.app_id && <> · 📱 {APP_NAMES[t.app_id] || t.app_id}</>}
                {t.locale && <> · 🌐 {t.locale}</>}
                {t.marketing && <> · ✅ marketing</>}
              </div>

              <div style={{ fontSize: "14px", color: "var(--text-primary)", whiteSpace: "pre-wrap", margin: "8px 0", lineHeight: 1.5 }}>
                {t.description}
              </div>

              <div style={{ fontSize: "11px", color: "var(--text-dim)", marginBottom: "10px" }}>
                {new Date(t.created_at).toLocaleString()}
              </div>

              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {STATUSES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => handleStatusChange(t.id, s.id)}
                    disabled={t.status === s.id}
                    style={{
                      padding: "5px 10px", borderRadius: "8px", fontSize: "12px", fontWeight: "600",
                      cursor: t.status === s.id ? "default" : "pointer",
                      border: `1px solid ${s.color}`, opacity: t.status === s.id ? 0.4 : 1,
                      background: "transparent", color: s.color,
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
